import { emailTemplateApi, emailTemplateMetricApi, eplApi } from '@atheneum/api'

import { renderExperience } from '@atheneum/api/dist/lib'
import handlebars from 'handlebars'
import get from 'lodash/get'
import reduce from 'lodash/reduce'
import { computed, extendObservable } from 'mobx'

handlebars.registerHelper('increment', function (value, options) {
  return parseInt(value) + 1
})

// This is what Froala would generate for newline element
// if wrapping content is reconfigured to div instead of p
const froalaSpace = '<div><br></div>'

const getRelevantPositionOnly = (epl, templateName) => {
  const content = `<b>${get(
    epl.relevantExperience.experience.position,
    'name',
    'Unknown position'
  )}, ${renderExperience(epl.relevantExperience.experience)}</b>`

  const result = templateName === 'Template 9' ? content : `<div>${content}</div>`
  return result
}

const getRelevantPositionAndCurrentPosition = epl => {
  const hiddenExpIds = (epl.hiddenExperiences || []).map(exp => exp.id)
  const relevantExperienceId = epl.relevantExperience.experienceId
  const filteredExperiences = (
    epl.cv?.experiences || epl.expert.cv.experiences
  ).filter(e => !hiddenExpIds.includes(e.id))

  const relevantPositionOnly = `<div>${get(
    epl.relevantExperience.experience.position,
    'name',
    'Unknown position'
  )}, ${renderExperience(epl.relevantExperience.experience)}</div>${froalaSpace}`

  const filtCurrentPositions = filteredExperiences.filter(exp => exp.current)
  const currentPositionForRelAndCurr = formatCurrentPositionForRelAndCurr(
    filtCurrentPositions,
    relevantExperienceId
  )
  return `
    ${relevantPositionOnly}
    <div style="margin: 0 0 5px 0">
      <b>All current positions: </b>
    </div>
    ${currentPositionForRelAndCurr}
    ${froalaSpace}
  `
}

const getPositions = (epl, templateName) => {
  const hiddenExpIds = (epl.hiddenExperiences || []).map(exp => exp.id)
  const relevantExperienceId = epl.relevantExperience.experienceId

  const filteredExperiences = (
    epl.cv?.experiences || epl.expert?.cv.experiences
  ).filter(e => !hiddenExpIds.includes(e.id))

  const allPositions = formatPositions(filteredExperiences)
  const relevantPositionOnly = getRelevantPositionOnly(epl, templateName)

  const currentPositionOnly = formatCurrentPositionOnly(
    filteredExperiences.filter(exp => exp.current)
  )

  const relevantPositionAndCurrentPosition = getRelevantPositionAndCurrentPosition(
    epl
  )

  const allPositionsExceptHidden = formatPositions(
    filteredExperiences,
    relevantExperienceId
  )
  return {
    allPositions,
    relevantPositionOnly,
    currentPositionOnly,
    relevantPositionAndCurrentPosition,
    allPositionsExceptHidden
  }
}

const replacePWithSpan = string => {
  return string.replaceAll('<p>', '<span>')
    .replaceAll('</p>', '</span>')
}

class extractEplStore {
  constructor(projectStore, staticData, userStore) {
    extendObservable(this, {
      loading: false,
      loadingPreview: true,
      selectedEpls: [],
      selectedTemplateType: null,
      templateOptions: [],
      selectedTemplate: null,
      epls: [],
      placeholders: [],
      templatePreview: {},
      projectStore,
      staticData,
      userStore,
      from: userStore.user,
      clients: computed(() =>
        projectStore.project.clients.filter(client => client.status)
      ),
      atheneumContacts: computed(() =>
        projectStore.project.employees.filter(employee => employee.status)
      ),
      metrics: null
    })
    this.groupName = 'EPL_extract'
  }

  getTemplateOptions() {
    this.loading = true
    emailTemplateApi.getByGroup(this.groupName).then(templates => {
      const templateOptions = templates.map(t => {
        return {
          id: t.id,
          name: t.name
        }
      })
      this.templateOptions.replace(templateOptions)
      this.setTemplate(templateOptions[0])
      this.loading = false
    })
  }

  async generateAndSavePreview(options = false) {
    this.loadingPreview = true
    const { projectName, office } = this.projectStore.project
    const formRecipients = options || [...this.clients, ...this.atheneumContacts]

    if (!this.selectedTemplate) return null

    const compiledSubject = handlebars.compile(this.selectedTemplate.subject)
    const compiledContent = handlebars.compile(this.selectedTemplate.content)

    let selectedEpls = this.selectedEpls

    const metric = await this.manageMetrics()

    const epls = await Promise.all(
      selectedEpls
        .filter(e => !e.hiddenFromClient)
        .map(async _epl => {
          const epl = removeHiddenFields(_epl)

          const {
            allPositions,
            relevantPositionOnly,
            currentPositionOnly,
            relevantPositionAndCurrentPosition,
            allPositionsExceptHidden
          } = getPositions(epl, this.selectedTemplate.name)

          const isExpertNameShown =
            !epl.hideExpertName &&
            office.account.parentAccount.expertsDisclosed

          const expertFullName = isExpertNameShown
            ? epl.expert.fullName
            : `Expert from ${
                this.staticData.names.countries[epl.expert.address.countryId]
              }`

          const { externLink } = await eplApi.getExternLink(epl.id, true)

          let availability = '<i>Upon request</i>'
          if (epl.expertAvailability) {
            availability = replacePWithSpan(epl.expertAvailability)
              .split('</span>')
              .join('</span>, ')
              .slice(0, -2)
          }

          let query = ''
          if(formRecipients.length === 1 && formRecipients[0].client) {
            let buff = Buffer.from(formRecipients[0].id.toString())
            const cryptedUserId = buff.toString('base64')
            query = '?cid=' + cryptedUserId
          }

          const data = {
            expert: {
              fullName: expertFullName,
              location: this.staticData.names.countries[
                epl.expert.address.countryId
              ],
              hyperlinkedFullName: `<a href="${externLink + query}" style="color: #3A86BF; text-decoration: none; cursor: pointer">Link to Atheneum profile</a>`
            },
            availability,
            expertProjectLink: {
              screening: replacePWithSpan(epl.screening || '<i>Not available</i>')
            },
            segmentName: epl.segment.name,
            industryExperience: replacePWithSpan(epl.industryExpertise || '<i>Not available</i>'),
            relevantPositionOnly,
            currentPositionOnly,
            relevantPositionAndCurrentPosition,
            allPositions,
            allPositionsExceptHidden
          }

          if (isExpertNameShown) {
            data.expert.title = epl.expert.title
          }

          if (this.selectedTemplate.name === 'Template 9') {
            data.actions = this.setActions(epl, externLink, metric.id)
            data.geography = epl.expert.address.country.name
          }

          return data
        })
    )

    this.epls.replace(epls)
    
    const formClients = formRecipients.filter(r => r.client)
    const clients = formClients.map(client => {
      const data = {
        client: {
          firstName: client.firstName,
          lastName: client.lastName
        }
      }

      if (client.title) {
        data.client.title = client.title
      }

      return data
    })

    const subject = compiledSubject({ projectName })
    const content = compiledContent({ clients, epls, projectName, froalaSpace })

    this.templatePreview = { subject, content }

    this.loadingPreview = false
  }

  async manageMetrics() {
    const payload = {
      emailTemplateId: this.selectedTemplate.id,
      projectId: this.projectStore.project.id,
      employeeId: this.userStore.user.employee.userId
    }

    const existingMetric = await emailTemplateMetricApi.get(payload)

    if (existingMetric) {
      return existingMetric
    }

    const createdMetric = await emailTemplateMetricApi.create(payload)
    return createdMetric
  }

  setActions = (epl, externLink, metricId) => {

    const allowedActions = [
      {
        eplStatusId: 6,
        name: 'Quick Request',
        clientInterfaceActionName: 'quick-request'
      },
      {
        eplStatusId: 8,
        name: 'Request with Availability',
        clientInterfaceActionName: 'request-scheduling'
      },
      {
        eplStatusId: 7,
        name: 'Reject',
        clientInterfaceActionName: 'reject-profile'
      }
    ]

    const actions = allowedActions.reduce((acc, action) => {
      acc.push({
        name: action.name,
        url: `${externLink}/?${action.clientInterfaceActionName}=${metricId}`
      })
      return acc
    }, [])

    return actions
  }

  setTemplate = template => {
    this.selectedTemplateType = template
    emailTemplateApi.get(template.id).then(selectedTemplate => {
      this.selectedTemplate = selectedTemplate
      this.highlightPlaceholders()
      this.generateAndSavePreview()
    })
  }

  resetTemplate = () => {
    emailTemplateApi.get(this.selectedTemplate.id).then(selectedTemplate => {
      this.selectedTemplate = selectedTemplate
    })
  }

  highlightPlaceholders() {
    this.placeholders.replace(
      JSON.parse(this.selectedTemplate.placeholders).map(pl => {
        const placeholders = pl.placeholders.map(placeholder => {
          if (this.selectedTemplate.content.includes(placeholder)) {
            placeholder = `<div style="font-weight: 800">${placeholder}</div>`
          }
          return placeholder
        })
        return { ...pl, placeholders }
      })
    )
  }
}

export default extractEplStore

function formatPositions(experiences, relevantExpId) {
  let positions = '<div>'
  experiences.forEach(exp => {
    const position = `${get(
      exp.position,
      'name',
      'Unknown position'
    )}, ${renderExperience(exp)}`
    positions = positions.concat(
      exp.id === relevantExpId
        ? `<div><b>${position}</b></div>`
        : `<div>${position}</div>`
    )
  })
  positions = positions.concat('</div>')

  return positions
}

function formatCurrentPositionOnly(currentPositions) {
  let currentPositionOnly = '<i>No current positions</i>'
  if (currentPositions.length) {
    currentPositionOnly = '<div>'
    currentPositions.forEach(exp => {
      const position = `${get(
        exp.position,
        'name',
        'Unknown Position'
      )}, ${renderExperience(exp)}`
      currentPositionOnly = currentPositionOnly.concat(`<div>${position}</div>`)
    })
    currentPositionOnly = currentPositionOnly.concat('</div>')
  }

  return currentPositionOnly
}

function formatCurrentPositionForRelAndCurr(currentPositions, relevantExpId) {
  let currentPositionForRelAndCurr = '<i>No current positions</i>'
  if (currentPositions.length) {
    currentPositionForRelAndCurr = '<div>'
    currentPositions.forEach(exp => {
      const position = `${get(
        exp.position,
        'name',
        'Unknown Position'
      )}, ${renderExperience(exp)}`
      currentPositionForRelAndCurr = currentPositionForRelAndCurr.concat(
        exp.id === relevantExpId
          ? `<div><b>${position}</b></div>`
          : `<div>${position}</div>`
      )
    })
    currentPositionForRelAndCurr = currentPositionForRelAndCurr.concat('</div>')
  }

  return currentPositionForRelAndCurr
}

function removeHiddenFields(epl) {
  const hideKeys = listOfKeys =>
    reduce(
      listOfKeys,
      (total, value, key) => {
        if (value) {
          total.push(key)
        }
        return total
      },
      []
    )

  let experiencesToHide = {}
  if (epl.relevantExperience.experiencesHiddenToExpert) {
    experiencesToHide = JSON.parse(
      epl.relevantExperience.experiencesHiddenToExpert
    )
  }

  if (experiencesToHide[epl.relevantExperience.experience.id]) {
    const keysToHide = hideKeys(
      experiencesToHide[epl.relevantExperience.experience.id]
    )

    if (keysToHide.includes('position')) {
      epl.relevantExperience.experience.position.name = undefined
      epl.relevantExperience.experience.description = undefined
    }

    if (keysToHide.includes('date')) {
      epl.relevantExperience.experience.startDate = undefined
      epl.relevantExperience.experience.endDate = undefined
    }

    if (keysToHide.includes('companyName')) {
      epl.relevantExperience.experience.company.name = undefined
    }
  }

  const removeExperiences = expertExperiencesToHide => experience => {
    if (!expertExperiencesToHide[experience.id]) {
      return experience
    }

    const keysToHide = hideKeys(expertExperiencesToHide[experience.id])

    if (keysToHide.includes('position')) {
      experience.position.name = undefined
      experience.description = undefined
    }

    if (keysToHide.includes('date')) {
      experience.startDate = undefined
      experience.endDate = undefined
    }

    if (keysToHide.includes('companyName')) {
      experience.company.name = undefined
    }

    return experience
  }

  if (epl.cv) {
    epl.cv.experiences = epl.cv.experiences.map(
      removeExperiences(experiencesToHide)
    )
  }

  if (get(epl.expert.experiences)) {
    epl.expert.experiences = epl.expert.experiences.map(
      removeExperiences(experiencesToHide)
    )
  }

  if (epl.fieldsHiddenToExpert) {
    JSON.parse(epl.fieldsHiddenToExpert).forEach(field => {
      epl[field] = undefined

      // special cases
      if (field === 'languages') {
        epl.expert['languagesToUser'] = undefined
        epl.expert.languages = undefined
      }

      if (field === 'expertAvailability') {
        epl['expertAvailabilityToEpls'] = undefined
      }

      if (field === 'industryExpertise' && epl.epl) {
        epl.epl.industryExpertise = undefined
      }

      if (field === 'fee') {
        epl.credits = undefined
      }
    })
  }

  return epl
}
